import Grid from '@mui/material/Grid';

import { useEffect, useState } from 'react';
import { useGetVinSearchQuery } from '@/features/api/search.api';
import { useDebounce } from '@/utils/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Spinner from '../Shared/Spinner';
import { Box, Paper, Typography, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  setAssetTableFiltered,
  setAssetTableSearchFilter,
} from '@/features/filters/filtersSlice';
import { RootState } from '@/app/store';
import SearchBar from './SearchBar';
import Can from '@/app/Auth/Can';
import { Action, Subject } from '@/types/enums';
import { UserState } from '@/types/types';
import { useGetAliasByIdQuery } from '@/features/api/alias.api';

const SearchPage = () => {
  const dispatch = useAppDispatch();
  const search = useAppSelector(
    (state: RootState) => state.filters.assetTable.search,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const urlSiteId = useParams()?.siteId?.toLowerCase() || '';

  const { siteMetadata: siteIdMetadata }: UserState = useAppSelector(
    (state: any) => state.user,
  );
  const isDesktop = useMediaQuery('(min-width:900px)');
  useState<boolean>(false);
  const navigate = useNavigate();
  const filteredAssets = useAppSelector(
    (state) => state.filters.assetTable.assetFiltered,
  );
  const siteId = useAppSelector((state: RootState) => state.user.siteId);
  const [t] = useTranslation();
  const params = useDebounce(search, 0);
  const skip = !params || params.length === 0 || params != search;

  const { data: alias } = useGetAliasByIdQuery(
    { siteId: urlSiteId || siteId, aliasId: searchParams.get('zq') },
    { skip: !siteId || !searchParams.get('zq') },
  );

  useEffect(() => {
    if (!alias?.data) return;
    dispatch(setAssetTableSearchFilter(alias.data));
    searchParams.delete('zq');
    setSearchParams(searchParams);
  }, [alias]);

  const {
    data: assetSearchData,
    isLoading,
    isSuccess,
    isError,
    isFetching: isFetchingAssets,
  } = useGetVinSearchQuery(
    {
      siteId: urlSiteId || siteId,
      params,
    },
    {
      skip,
    },
  );

  useEffect(() => {
    const queryParams: string = Object.fromEntries([...searchParams])?.q;
    if (!queryParams || queryParams === '') return;
    dispatch(setAssetTableSearchFilter(queryParams));
    searchParams.delete('q');
    setSearchParams(searchParams);
  }, [searchParams]);

  useEffect(() => {
    if (!assetSearchData) return;
    dispatch(setAssetTableFiltered(assetSearchData));
    if (assetSearchData && assetSearchData.length > 0) {
      navigate(`/${siteId}/assets`, { state: filteredAssets });
    }
  }, [assetSearchData, search]);

  if (isLoading || isFetchingAssets) {
    return <Spinner />;
  }

  return (
    <Box
      data-testid="asset-page-container"
      className="datatable-map-container"
      sx={{
        overflow: 'auto',
        maxWidth: '100vw',
      }}
    >
      <Grid
        data-testid="search-container"
        container
        sx={{
          overflow: !isDesktop ? 'hidden' : '',
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Paper
          sx={{
            minWidth: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        >
          <Box
            data-testid="landing-page-container"
            sx={{
              overflow: 'auto',
            }}
          >
            <Box
              component="img"
              data-testid="main-logo-image"
              alt={siteIdMetadata.name}
              width="auto"
              height="150px"
              marginRight="10px"
              paddingTop="40px"
              paddingBottom="10px"
              maxWidth="90%"
              src={
                siteIdMetadata.largeLogoLocation === null
                  ? 'https://storage.googleapis.com/cox2m-dealers-ui-logos/cog-114x114.png'
                  : siteIdMetadata.largeLogoLocation
              }
            />
            <Typography
              sx={(theme) => ({
                paddingRight: !isDesktop ? '5px' : '0px',
                paddingLeft: !isDesktop ? '5px' : '0px',
                flex: 1,
                fontSize: '20px',
                fontWeight: '600',
                color: theme.palette.text.primary,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              })}
            >
              {t('searchBar.welcome')}
            </Typography>
            <SearchBar search={search} />
            {(isSuccess &&
              assetSearchData?.length === 0 &&
              !isFetchingAssets) ||
            isError ? (
              <Box
                display="flex"
                flexDirection="column"
                height="100%"
                alignItems="center"
                justifyContent="center"
                paddingBottom="30px"
                data-testid="not-found-message"
              >
                <Typography
                  component="h3"
                  fontWeight="600"
                  fontSize="16px"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    margin: '12px 0',
                  })}
                >
                  {t('searchBar.notResultsFound')}
                </Typography>
                <Typography
                  component="p"
                  fontSize="14px"
                  sx={(theme) => ({ color: theme.palette.text.secondary })}
                >
                  {t('searchBar.notResultsFoundHelp')}
                </Typography>
                <Typography
                  component="p"
                  fontSize="14px"
                  sx={(theme) => ({ color: theme.palette.text.secondary })}
                >
                  {t('global.noResultsFoundExample')}
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={(theme) => ({
                  flex: 1,
                  fontSize: '14px',
                  fontWeight: '400',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingBottom: '30px',
                })}
              >
                {`${t('searchBar.enterVinLabel')}${
                  Can(Action.READ, Subject.stock_number)
                    ? t('searchBar.stock')
                    : ''
                }${
                  Can(Action.READ, Subject.work_order)
                    ? t('searchBar.workOrder')
                    : ''
                } ${
                  Can(Action.READ, Subject.seller_id)
                    ? t('searchBar.sellerId')
                    : ''
                } ${t('searchBar.instructions')}`}
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    </Box>
  );
};

export default SearchPage;
